import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const CartContext = createContext();

// Used until the real settings load from Supabase, and as a fallback if that row is ever missing
const DEFAULT_SHIPPING = { free_shipping_threshold: 500, flat_shipping_charge: 60 };

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('bhumi_cart');
      const parsed = saved ? JSON.parse(saved) : [];
      // Older carts have no cartKey — fall back to product id
      return parsed.map(item => ({ ...item, cartKey: item.cartKey || item.id }));
    } catch { return []; }
  });

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [shippingSettings, setShippingSettings] = useState(DEFAULT_SHIPPING);

  useEffect(() => {
    supabase.from('shipping_settings').select('free_shipping_threshold, flat_shipping_charge').eq('id', 1).single()
      .then(({ data }) => { if (data) setShippingSettings(data); });
  }, []);

  useEffect(() => {
    localStorage.setItem('bhumi_cart', JSON.stringify(cart));
  }, [cart]);

  // Same product in two sizes = two separate cart lines (keyed by product id + size)
  const addToCart = (product, quantity = 1, variant = null) => {
    const cartKey = variant ? `${product.id}::${variant.size}` : product.id;
    setCart(prev => {
      const existing = prev.find(item => item.cartKey === cartKey);
      if (existing) {
        return prev.map(item =>
          item.cartKey === cartKey
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      const entry = variant
        ? {
            ...product,
            cartKey,
            quantity,
            variant_size: variant.size,
            price: Number(variant.price) || 0,
            original_price: Number(variant.original_price) || 0,
            unit: variant.size,
          }
        : { ...product, cartKey, quantity };
      return [...prev, entry];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (cartKey) => {
    setCart(prev => prev.filter(item => item.cartKey !== cartKey));
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity <= 0) { removeFromCart(cartKey); return; }
    setCart(prev =>
      prev.map(item => item.cartKey === cartKey ? { ...item, quantity } : item)
    );
  };

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);

  const freeShippingThreshold = Number(shippingSettings.free_shipping_threshold);
  const flatShippingCharge = Number(shippingSettings.flat_shipping_charge);
  const shipping = cartTotal > freeShippingThreshold ? 0 : cartTotal > 0 ? flatShippingCharge : 0;

  return (
    <CartContext.Provider value={{
      cart, addToCart, removeFromCart, updateQuantity, clearCart,
      cartCount, cartTotal, isCartOpen, setIsCartOpen,
      shipping, freeShippingThreshold, flatShippingCharge
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
